import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Wrench, Check } from "lucide-react";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "");

export default function PendingDismantlesBanner() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showOld, setShowOld] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const load = () => api.get("/dashboard/pending-dismantles").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const nextDay = (iso) => new Date(new Date(iso + "T00:00:00Z").getTime() + 86400000).toISOString().slice(0, 10);

  const markDismantled = async (r, retro = false) => {
    const dDate = retro && r.end_date ? nextDay(r.end_date) : new Date().toISOString().slice(0, 10);
    const who = r.persons?.length ? `\nDemontat de: ${r.persons.join(", ")}` : "\n(fără echipă planificată — se marchează fără persoane)";
    const when = retro ? `\nData demontării: ${fmtD(dDate)} (ziua următoare finalizării)` : "";
    if (!window.confirm(`Marchezi „${r.name}" ca DEMONTAT${retro ? " ÎN TRECUT" : ""}?${when}${who}`)) return;
    setBusyId(r.id);
    try {
      await api.patch(`/projects/${r.id}/status`, {
        status: "Demontat de Facturat",
        dismantled_by_person_ids: r.person_ids || [],
        dismantled_by_car_ids: r.car_ids || [],
        dismantled_note: retro ? "Demontat în trecut (curățare șantiere vechi)" : "Marcat ca demontat din bannerul Acasă",
        dismantle_date: dDate,
      });
      toast.success(`„${r.name}" marcat ca demontat → Demontat de Facturat`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la marcarea demontajului");
    } finally { setBusyId(null); }
  };

  const cleanupAll = async () => {
    if (!window.confirm(`Marchezi TOATE șantierele terminate până săptămâna trecută ca DEMONTATE?\nData demontării = ziua următoare finalizării fiecăruia.`)) return;
    setCleaning(true);
    try {
      const r = await api.post("/dashboard/pending-dismantles/cleanup");
      toast.success(`${r.data?.count || 0} șantiere marcate ca demontate → Demontat de Facturat`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la curățare");
    } finally { setCleaning(false); }
  };

  if (!rows || rows.length === 0) return null;
  const daysAgo = (iso) => (Date.now() - new Date(iso + "T00:00:00").getTime()) / 86400000;
  const recent = rows.filter((r) => !r.auto_detected || !r.end_date || daysAgo(r.end_date) <= 14);
  const old = rows.filter((r) => !recent.includes(r));
  const shown = showOld ? [...recent, ...old] : recent;
  const unplanned = recent.filter((r) => !r.planned).length;
  const _now = new Date();
  const mondayIso = new Date(_now.getTime() - (((_now.getDay() + 6) % 7) * 86400000)).toISOString().slice(0, 10);
  const bulkEligible = rows.filter((r) => r.end_date && r.end_date < mondayIso).length;
  const theme = unplanned > 0
    ? { box: "border-red-400 bg-red-50", icon: "text-red-600", title: "text-red-900", chev: "text-red-700" }
    : { box: "border-orange-300 bg-orange-50", icon: "text-orange-600", title: "text-orange-900", chev: "text-orange-700" };

  return (
    <div className={`border rounded-lg overflow-hidden ${theme.box}`} data-testid="pending-dismantles-banner">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="pending-dismantles-toggle"
      >
        <div className="flex items-center gap-2">
          <Wrench size={18} className={theme.icon} />
          <span className={`font-bold text-sm ${theme.title}`}>
            🔧 Șantiere de demontat: {recent.length}
            {unplanned > 0 && <span className="ml-2 text-red-600 animate-pulse">⚠ {unplanned} FĂRĂ demontaj planificat!</span>}
          </span>
        </div>
        {open ? <ChevronUp size={16} className={theme.chev} /> : <ChevronDown size={16} className={theme.chev} />}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2">
          {shown.map((r) => (
            <div key={r.id} className={`bg-white border rounded-md px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 ${r.planned ? "border-orange-200" : "border-red-400 ring-1 ring-red-200"}`} data-testid={`pending-dismantle-${r.id}`}>
              <div className="min-w-[180px]">
                <Link to="/proiecte" className="font-bold text-sm text-zinc-900 hover:underline">{r.name}</Link>
                {r.adresa && <div className="text-[11px] text-zinc-500">📍 {r.adresa}</div>}
                {r.auto_detected && (
                  <span className="inline-block mt-0.5 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5" data-testid={`auto-detected-${r.id}`}>
                    🏁 Terminat {fmtD(r.end_date)} — trecut automat pe „De demontat"
                  </span>
                )}
              </div>
              {r.planned ? (
                <div className="text-xs text-zinc-700 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold text-emerald-700">📅 Demontaj: {fmtD(r.plan_start)}</span>
                  {r.persons.length > 0 && <span>👷 {r.persons.join(", ")}</span>}
                  {r.cars.length > 0 && <span>🚗 {r.cars.join(" · ")}</span>}
                  {r.persons.length === 0 && r.cars.length === 0 && <span className="text-zinc-400 italic">alocare fără persoane/mașini</span>}
                </div>
              ) : (
                <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded px-2 py-0.5">⚠ Nimeni planificat — programează demontajul</span>
              )}
              <button
                onClick={() => markDismantled(r, old.includes(r))}
                disabled={busyId === r.id}
                className={`ml-auto inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded text-white transition-colors disabled:opacity-50 flex-shrink-0 ${old.includes(r) ? "bg-zinc-600 hover:bg-zinc-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                data-testid={`mark-dismantled-${r.id}`}
                title={old.includes(r) ? "Marchează demontat în trecut (data = ziua următoare finalizării)" : "Marchează șantierul ca demontat (trece la Demontat de Facturat)"}
              >
                {old.includes(r) ? <>🕰 {busyId === r.id ? "..." : "Demontat în trecut"}</> : <><Check size={13} /> {busyId === r.id ? "..." : "S-a demontat"}</>}
              </button>
            </div>
          ))}
          {old.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowOld((v) => !v)}
                className="text-xs font-semibold text-zinc-600 hover:text-zinc-900 underline"
                data-testid="toggle-old-dismantles"
              >
                {showOld ? "Ascunde șantierele mai vechi" : `🗄 Arată încă ${old.length} șantiere terminate mai demult (posibil deja demontate)`}
              </button>
              {bulkEligible > 0 && (
                <button
                  onClick={cleanupAll}
                  disabled={cleaning}
                  className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded bg-zinc-700 text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  data-testid="cleanup-old-dismantles"
                  title="Marchează în serie toate șantierele terminate până săptămâna trecută ca demontate (data = ziua următoare finalizării)"
                >
                  🕰 {cleaning ? "Se curăță..." : `Marchează toate ${bulkEligible} ca demontate (până săpt. trecută)`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
